import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { hariIniISO } from "../lib/tanggal";
import { useAuth } from "../context/AuthContext";
import { FileText, Plus, Search, X, Printer, Ban, Pencil, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import LoadingState from "../components/ui/loading-state";
import MemoPrint from "../components/faktur/MemoPrint";
import PemilihTemplate from "../components/faktur/PemilihTemplate";

// Internal Memorandum — permintaan owner 26 Agu 2026 (terbit otomatis dari
// selisih blind opname) dan 29 Agu 2026 (Admin: "Cetak IM").
//
// Memo yang terbit otomatis TIDAK ADA GUNANYA kalau tidak bisa dilihat dan
// dicetak, jadi halaman ini bagian dari fitur itu, bukan tambahan.
//
// Brief 4 Sep 2026 P13: memo MANUAL wajib approval. Ia lahir "menunggu
// approval", disetujui/ditolak pemegang izin `internal_memo.update` (kunci
// yang SAMA dengan `require_permission` di backend — tombolnya tidak boleh
// berujung 403), dan baru bisa dicetak setelah terbit. Memo otomatis tetap
// terbit langsung — ia bukti sistem, bukan pernyataan orang.

const JENIS_LABEL = {
  opname_selisih: { l: "Selisih Opname", c: "bg-amber-50 text-amber-700 border-amber-200" },
  manual: { l: "Manual", c: "bg-slate-50 text-slate-600 border-slate-200" },
};

// Peta label status LENGKAP. Memo lama produksi tidak punya status selain
// "terbit"/"batal"; dokumen tanpa status dibaca "terbit" (kompat).
const STATUS_LABEL = {
  menunggu_approval: { l: "Menunggu Approval", c: "bg-amber-50 text-amber-700 border-amber-200" },
  terbit: { l: "Terbit", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ditolak: { l: "Ditolak", c: "bg-rose-50 text-rose-700 border-rose-200" },
  batal: { l: "Dibatalkan", c: "bg-slate-100 text-slate-600 border-slate-200" },
};
const statusMemo = (m) => m?.status || "terbit";
const infoStatus = (m) => STATUS_LABEL[statusMemo(m)] || STATUS_LABEL.terbit;

const ROLE_ISTIMEWA = ["superadmin", "admin"];

export default function InternalMemo() {
  const { can, user } = useAuth();
  // Approver yang HANYA dicentang kunci approval-nya tetap harus bisa membuka
  // daftar — backend `_boleh_lihat_memo` memakai OR yang sama.
  const bolehLihat = can("inventory_opname", "view") || can("internal_memo", "update");
  const bolehTulis = can("inventory_opname", "create");
  const bolehApprove = can("internal_memo", "update");
  // Endpoint data cetak masih digerbangi `inventory_opname.view`.
  const bolehCetak = can("inventory_opname", "view");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState({ q: "", jenis: "", status: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);
  const [formBaru, setFormBaru] = useState(null);

  const muat = useCallback(async () => {
    if (!bolehLihat) { setLoading(false); return; }
    setLoading(true); setLoadError(null);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/internal-memo", { params });
      setItems(r.data.items || []);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
  }, [bolehLihat, filter]);
  useEffect(() => { muat(); }, [muat]);

  if (!bolehLihat) {
    return <div className="p-8 text-sm text-slate-500">Tidak punya izin melihat Internal Memo.</div>;
  }

  const jumlahMenunggu = items.filter(m => statusMemo(m) === "menunggu_approval").length;

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="internal-memo">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Administrasi</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Internal Memo</h1>
          <p className="text-sm text-slate-600 mt-1">
            Memo resmi internal. Selisih blind opname menerbitkannya otomatis; memo manual
            diajukan lalu disetujui pemegang izin approval sebelum bisa dicetak.
          </p>
          {bolehApprove && jumlahMenunggu > 0 && (
            <p className="text-xs text-amber-700 mt-1" data-testid="im-jumlah-menunggu">
              {jumlahMenunggu} memo menunggu approval Anda.
            </p>
          )}
        </div>
        {bolehTulis && (
          <button onClick={() => setFormBaru({ judul: "", isi: "", tanggal: hariIniISO(), cabang: "" })}
                  data-testid="im-baru"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Memo Baru
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={filter.q} onChange={e => setFilter(f => ({ ...f, q: e.target.value }))}
                 placeholder="Cari nomor atau judul..." data-testid="im-cari"
                 className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-emerald-500" />
        </div>
        <select value={filter.jenis} onChange={e => setFilter(f => ({ ...f, jenis: e.target.value }))}
                data-testid="im-jenis" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
          <option value="">Semua jenis</option>
          <option value="opname_selisih">Selisih Opname</option>
          <option value="manual">Manual</option>
        </select>
        <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
                data-testid="im-status" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
          <option value="">Semua status</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
        </select>
        <div className="flex gap-2">
          <input type="date" value={filter.date_from} data-testid="im-dari"
                 onChange={e => setFilter(f => ({ ...f, date_from: e.target.value }))}
                 className="h-10 px-2 rounded-lg border border-slate-200 text-sm w-full" />
          <input type="date" value={filter.date_to} data-testid="im-sampai"
                 onChange={e => setFilter(f => ({ ...f, date_to: e.target.value }))}
                 className="h-10 px-2 rounded-lg border border-slate-200 text-sm w-full" />
        </div>
      </div>

      <LoadingState loading={loading} error={loadError} onRetry={muat} varian="tabel" data-testid="im-loading">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Nomor / Tanggal</th>
                <th className="text-left px-4 py-2.5">Judul</th>
                <th className="text-left px-4 py-2.5">Jenis</th>
                <th className="text-left px-4 py-2.5">Sumber</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={5} className="p-10 text-center text-sm text-slate-400">Belum ada internal memo</td></tr>
              ) : items.map(m => (
                <tr key={m.id} onClick={() => setDetail(m)} data-testid={`im-row-${m.nomor}`}
                    className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer">
                  <td className="px-4 py-3">
                    <p className="font-mono text-xs text-slate-700">{m.nomor}</p>
                    <p className="text-[11px] text-slate-500">{m.tanggal}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-900">
                    {m.judul}
                    {m.dibuat_oleh_nama && <p className="text-[11px] text-slate-400">oleh {m.dibuat_oleh_nama}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded border ${(JENIS_LABEL[m.jenis] || JENIS_LABEL.manual).c}`}>
                      {(JENIS_LABEL[m.jenis] || JENIS_LABEL.manual).l}
                    </span>
                    {m.otomatis && <span className="ml-1 text-[10px] text-slate-400">otomatis</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{m.sumber_ref_code || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded border ${infoStatus(m).c}`}
                          data-testid={`im-status-${m.nomor}`}>
                      {infoStatus(m).l}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadingState>

      {detail && <ModalDetail memo={detail} bolehTulis={bolehTulis} bolehApprove={bolehApprove}
                              bolehCetak={bolehCetak} user={user}
                              onClose={() => { setDetail(null); muat(); }} />}
      {formBaru && <ModalBaru form={formBaru} setForm={setFormBaru}
                              onSelesai={() => { setFormBaru(null); muat(); }} />}
    </div>
  );
}

function ModalDetail({ memo, bolehTulis, bolehApprove, bolehCetak, user, onClose }) {
  const [edit, setEdit] = useState(false);
  const [isi, setIsi] = useState(memo.isi || "");
  const [judul, setJudul] = useState(memo.judul || "");
  const [busy, setBusy] = useState(false);
  const [tolak, setTolak] = useState(null); // { alasan } saat modal tolak terbuka
  // Alur cetak mengikuti pola yang SUDAH ADA di OpnameBlind: pilih template
  // dulu kalau ada lebih dari satu, baru render. Bukan mekanisme kedua.
  const [cetakan, setCetakan] = useState(null);

  const status = statusMemo(memo);
  const manual = !memo.otomatis;
  const emailSaya = (user?.email || "").toLowerCase();
  const pembuatSaya = !!emailSaya && (memo.dibuat_oleh || "").toLowerCase() === emailSaya;
  const roleIstimewa = ROLE_ISTIMEWA.includes(user?.role);

  // Cermin gerbang backend (internal_memo.py) — setiap tombol di bawah hanya
  // tampil bila endpoint-nya tidak akan menjawab 403/409:
  // - Sunting: menunggu/ditolak (manual) atau terbit (otomatis, kompat); oleh
  //   pembuat atau approver; memo otomatis oleh siapa pun ber-create.
  // - Batal: manual terbit → pembuat/approver; status lain → create/approver.
  // - Setujui/Tolak: approver, hanya menunggu_approval, bukan pembuat sendiri
  //   (kecuali role istimewa — jalur darurat yang dicatat backend).
  // - Cetak: hanya terbit/batal (menunggu/ditolak → endpoint 409).
  const manualTerbit = manual && status === "terbit";
  const bolehSunting = status !== "batal" && !manualTerbit &&
    (memo.otomatis ? bolehTulis : (bolehApprove || (bolehTulis && pembuatSaya)));
  const bolehBatal = status !== "batal" &&
    (bolehApprove || (bolehTulis && (!manualTerbit || pembuatSaya)));
  const bolehPutuskan = bolehApprove && status === "menunggu_approval" && (!pembuatSaya || roleIstimewa);
  const tombolCetak = bolehCetak && (status === "terbit" || status === "batal");

  const bukaCetak = async () => {
    setCetakan({ step: "memuat", templates: [], templateId: null, data: null });
    try {
      const { data } = await api.get("/faktur/templates", { params: { jenis: "internal_memo" } });
      const daftar = data?.items || data || [];
      if (!daftar.length) { setCetakan({ step: "kosong" }); return; }
      if (daftar.length === 1) { await muatDokumen(daftar[0].id); return; }
      setCetakan({ step: "pilih", templates: daftar, templateId: daftar[0].id, data: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat template cetak");
      setCetakan(null);
    }
  };

  const muatDokumen = async (templateId) => {
    setCetakan(c => ({ ...(c || {}), step: "preview", data: null }));
    try {
      const { data } = await api.get(`/faktur/data/internal-memo/${memo.id}`,
        { params: templateId ? { template_id: templateId } : {} });
      setCetakan(c => ({ ...(c || {}), step: "preview", data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat dokumen cetak");
      setCetakan(null);
    }
  };

  const simpan = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/internal-memo/${memo.id}`, { judul, isi });
      toast.success(data?.diajukan_ulang ? "Memo diperbaiki dan diajukan ulang — menunggu approval" : "Memo diperbarui ✓");
      setEdit(false);
      if (data?.diajukan_ulang) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  const batalkan = async () => {
    const alasan = window.prompt("Alasan pembatalan memo ini?");
    if (!alasan || alasan.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/internal-memo/${memo.id}/batal`, null, { params: { alasan: alasan.trim() } });
      toast.success("Memo dibatalkan");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); }
    finally { setBusy(false); }
  };

  const setujui = async () => {
    if (!window.confirm(`Setujui memo ${memo.nomor}? Setelah disetujui isinya tidak bisa disunting lagi.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/internal-memo/${memo.id}/approve`, {});
      toast.success(data?.message || "Memo disetujui ✓");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui"); }
    finally { setBusy(false); }
  };

  const kirimTolak = async () => {
    const alasan = (tolak?.alasan || "").trim();
    if (alasan.length < 3) { toast.error("Alasan penolakan minimal 3 karakter"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/internal-memo/${memo.id}/tolak`, { alasan });
      toast.success(data?.message || "Memo ditolak");
      setTolak(null);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="im-detail"
           className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-xs text-slate-500">
              {memo.nomor} · {memo.tanggal}
              <span className={`ml-2 text-[11px] px-2 py-0.5 rounded border ${infoStatus(memo).c}`}>{infoStatus(memo).l}</span>
            </p>
            {edit
              ? <input value={judul} onChange={e => setJudul(e.target.value)} data-testid="im-edit-judul"
                       className="mt-1 w-full h-9 px-2 border border-slate-200 rounded text-base font-semibold" />
              : <h3 className="font-display font-bold text-lg truncate">{memo.judul}</h3>}
            {memo.gudang && <p className="text-xs text-slate-500 mt-0.5">{memo.gudang}</p>}
            {memo.dibuat_oleh_nama && <p className="text-xs text-slate-500 mt-0.5">Dibuat oleh {memo.dibuat_oleh_nama}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {status === "menunggu_approval" && (
            <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="im-banner-menunggu">
              Memo ini <b>menunggu approval</b> pemegang izin "Internal Memo — Approval".
              Belum bisa dicetak sampai disetujui.
            </div>
          )}
          {status === "ditolak" && (
            <div className="mb-3 bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800" data-testid="im-banner-ditolak">
              Memo ini <b>ditolak</b>{memo.ditolak_oleh_nama ? ` oleh ${memo.ditolak_oleh_nama}` : ""}
              {memo.ditolak_alasan ? ` — ${memo.ditolak_alasan}` : ""}.
              {pembuatSaya && " Perbaiki lalu simpan untuk mengajukannya kembali."}
            </div>
          )}
          {status === "terbit" && memo.disetujui_oleh_nama && (
            <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="im-banner-terbit">
              Disetujui oleh <b>{memo.disetujui_oleh_nama}</b>{memo.disetujui_pada ? ` · ${String(memo.disetujui_pada).slice(0, 16).replace("T", " ")}` : ""}.
              {memo.self_approved_note && <span className="block mt-1 text-amber-700">{memo.self_approved_note}</span>}
            </div>
          )}
          {status === "batal" && (
            <div className="mb-3 bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800">
              Memo ini <b>dibatalkan</b>{memo.batal_alasan ? ` — ${memo.batal_alasan}` : ""}
              {memo.batal_oleh ? ` (${memo.batal_oleh})` : ""}.
              Nomornya sengaja tidak dihapus supaya cetakan yang sudah beredar tetap bisa dilacak.
            </div>
          )}
          {edit
            ? <textarea value={isi} onChange={e => setIsi(e.target.value)} rows={18} data-testid="im-edit-isi"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:border-emerald-500" />
            : <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans" data-testid="im-isi">{memo.isi}</pre>}
        </div>

        <div className="p-4 border-t border-slate-100 flex flex-wrap justify-end gap-2">
          {tombolCetak && (
            <button onClick={bukaCetak} data-testid="im-cetak"
                    className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm inline-flex items-center gap-2">
              <Printer className="w-4 h-4" /> Cetak
            </button>
          )}
          {bolehPutuskan && !edit && (
            <>
              <button onClick={() => setTolak({ alasan: "" })} disabled={busy} data-testid="im-tolak"
                      className="h-10 px-4 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm inline-flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Tolak
              </button>
              <button onClick={setujui} disabled={busy} data-testid="im-setujui"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
                <Check className="w-4 h-4" /> Setujui
              </button>
            </>
          )}
          {edit ? (
            <>
              <button onClick={() => { setEdit(false); setIsi(memo.isi); setJudul(memo.judul); }}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal Edit</button>
              <button onClick={simpan} disabled={busy} data-testid="im-simpan"
                      className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "..." : (status === "ditolak" ? "Simpan & Ajukan Ulang" : "Simpan")}
              </button>
            </>
          ) : (
            <>
              {bolehBatal && (
                <button onClick={batalkan} disabled={busy} data-testid="im-batalkan"
                        className="h-10 px-4 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm inline-flex items-center gap-2">
                  <Ban className="w-4 h-4" /> Batalkan
                </button>
              )}
              {bolehSunting && (
                <button onClick={() => setEdit(true)} data-testid="im-edit"
                        className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium inline-flex items-center gap-2">
                  <Pencil className="w-4 h-4" /> Sunting
                </button>
              )}
            </>
          )}
        </div>

        {tolak && (
          <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={() => setTolak(null)}>
            <div onClick={e => e.stopPropagation()} data-testid="im-tolak-modal"
                 className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-3">
              <h3 className="font-display font-bold text-lg">Tolak memo {memo.nomor}</h3>
              <p className="text-xs text-slate-500">
                Alasan wajib — pembuat perlu tahu apa yang harus diperbaiki sebelum mengajukan ulang.
              </p>
              <textarea value={tolak.alasan} onChange={e => setTolak({ alasan: e.target.value })} rows={4}
                        placeholder="Alasan penolakan (minimal 3 karakter)" data-testid="im-tolak-alasan"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-rose-400" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setTolak(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={kirimTolak} disabled={busy || (tolak.alasan || "").trim().length < 3} data-testid="im-tolak-kirim"
                        className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-50">
                  {busy ? "..." : "Tolak Memo"}
                </button>
              </div>
            </div>
          </div>
        )}

        {cetakan && (
          <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4 overflow-y-auto"
               onClick={() => setCetakan(null)}>
            <div onClick={e => e.stopPropagation()} data-testid="im-cetak-modal"
                 className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl">
              <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                <h3 className="font-display font-semibold inline-flex items-center gap-2">
                  <Printer className="w-4 h-4" /> Cetak Internal Memo
                </h3>
                <button onClick={() => setCetakan(null)} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6">
                {cetakan.step === "kosong" ? (
                  <p className="text-sm text-slate-500 text-center py-8" data-testid="im-cetak-kosong">
                    Belum ada template Internal Memo. Buat dulu di menu Template Faktur &amp; Struk
                    (pilih jenis "internal_memo").
                  </p>
                ) : cetakan.step === "pilih" ? (
                  <div className="space-y-4">
                    <PemilihTemplate templates={cetakan.templates} value={cetakan.templateId}
                      onChange={(id) => setCetakan(c => ({ ...c, templateId: id }))}
                      jenisLabel="internal memo" testidPrefix="im-cetak-tpl" />
                    <div className="flex justify-end">
                      <button onClick={() => muatDokumen(cetakan.templateId)} data-testid="im-cetak-lanjut"
                              className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                        Lanjutkan
                      </button>
                    </div>
                  </div>
                ) : !cetakan.data ? (
                  <LoadingState loading jumlahKartu={1} data-testid="im-cetak-loading" />
                ) : (
                  <MemoPrint memo={cetakan.data.memo} template={cetakan.data.template}
                             noFaktur={cetakan.data.no_faktur} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ModalBaru({ form, setForm, onSelesai }) {
  const [busy, setBusy] = useState(false);
  const simpan = async () => {
    if (!form.judul.trim() || !form.isi.trim()) { toast.error("Judul dan isi wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post("/internal-memo", form);
      toast.success("Memo diajukan — menunggu approval ✓");
      onSelesai();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setForm(null)}>
      <div onClick={e => e.stopPropagation()} data-testid="im-form-baru"
           className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg inline-flex items-center gap-2">
            <FileText className="w-5 h-5" /> Internal Memo Baru
          </h3>
          <button onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-500">
          Memo manual diajukan dulu; ia terbit dan bisa dicetak setelah disetujui pemegang izin approval.
        </p>
        <input value={form.judul} onChange={e => setForm(f => ({ ...f, judul: e.target.value }))}
               placeholder="Judul memo" data-testid="im-baru-judul"
               className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={form.tanggal} data-testid="im-baru-tanggal"
                 onChange={e => setForm(f => ({ ...f, tanggal: e.target.value }))}
                 className="h-10 px-3 border border-slate-200 rounded-lg text-sm" />
          <input value={form.cabang} onChange={e => setForm(f => ({ ...f, cabang: e.target.value }))}
                 placeholder="Cabang (opsional)" data-testid="im-baru-cabang"
                 className="h-10 px-3 border border-slate-200 rounded-lg text-sm" />
        </div>
        <textarea value={form.isi} onChange={e => setForm(f => ({ ...f, isi: e.target.value }))}
                  rows={10} placeholder="Isi memo" data-testid="im-baru-isi"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        <div className="flex justify-end gap-2">
          <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={simpan} disabled={busy} data-testid="im-baru-simpan"
                  className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "..." : "Ajukan"}
          </button>
        </div>
      </div>
    </div>
  );
}
